import React from "react";
import { motion } from "framer-motion";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Avatar,
  Paper,
  Checkbox,
  TableSortLabel,
  Typography,
  Stack
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useKivunovaTranslation } from "@kivunova/kivufrontendcommon";
import type { Product } from "../types/products";
import { DESKTOP_COLUMNS, MOBILE_COLUMNS } from "./tableColumns";
import { formatSellingMode } from "../utils/productUtils";
import { SellingMode } from "../enums/sellingMode";

type Order = "asc" | "desc";

export type ProductListTableProps = {
  products: Product[];
  selectedIds: string[];
  order: Order;
  orderBy: keyof Product;
  onSort: (property: keyof Product) => void;
  onSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectOne: (id: string) => void;
  onViewProduct: (productId: string) => void;
  isMobile: boolean;
};

const ProductListTable: React.FC<ProductListTableProps> = ({
  products,
  selectedIds,
  order,
  orderBy,
  onSort,
  onSelectAll,
  onSelectOne,
  onViewProduct,
  isMobile
}) => {
  const { t } = useKivunovaTranslation();
  const columns = isMobile ? MOBILE_COLUMNS : DESKTOP_COLUMNS;

  const rowVariants = {
    hidden: { opacity: 0, y: 6 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.03, duration: 0.25 }
    })
  };

  return (
    <TableContainer
      component={Paper}
      data-testid="product-list-table"
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        boxShadow: "none"
      }}
    >
      <Table>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                color="primary"
                checked={
                  selectedIds.length === products.length && products.length > 0
                }
                indeterminate={
                  selectedIds.length > 0 && selectedIds.length < products.length
                }
                onChange={onSelectAll}
              />
            </TableCell>
            {columns.map((column) => (
              <TableCell
                key={column.id}
                sortDirection={orderBy === column.id ? order : false}
              >
                <TableSortLabel
                  active={orderBy === column.id}
                  direction={orderBy === column.id ? order : "asc"}
                  onClick={() => onSort(column.id)}
                  sx={{ fontSize: 13, fontWeight: 600 }}
                >
                  {t(`product-list-col-${String(column.id)}`, column.label)}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell sx={{ fontSize: 13, fontWeight: 600 }} align="center">
              {t("product-list-col-actions", "Actions")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {products.map((product, index) => (
            <TableRow
              key={product.id}
              component={motion.tr}
              variants={rowVariants}
              initial="hidden"
              animate="visible"
              custom={index}
              hover
              sx={{
                "&:hover": {
                  backgroundColor: "action.hover",
                  cursor: "pointer"
                }
              }}
            >
              <TableCell padding="checkbox">
                <Checkbox
                  color="primary"
                  checked={selectedIds.includes(product.id)}
                  onChange={() => onSelectOne(product.id)}
                />
              </TableCell>
              <TableCell>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {!isMobile && (
                    <Avatar
                      src={
                        product.brandName
                          ? `/images/products/${product.brandName}.png`
                          : undefined
                      }
                      sx={{ width: 30, height: 30 }}
                    />
                  )}
                  <Typography variant="body2" sx={{ fontSize: 13 }}>
                    {product.name}
                  </Typography>
                </Stack>
              </TableCell>
              {isMobile ? (
                <TableCell sx={{ fontSize: 13 }}>
                  {product.category ?? "N/A"}
                </TableCell>
              ) : (
                <>
                  <TableCell sx={{ fontSize: 13 }}>
                    {product.supplierId ?? "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {product.category ?? "N/A"}
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {product.sellingMode
                      ? formatSellingMode(product.sellingMode as SellingMode, t)
                      : "-"}
                  </TableCell>
                </>
              )}
              <TableCell align="center" sx={{ fontSize: 13 }}>
                <IconButton
                  color="primary"
                  aria-label={t("product-list-view-aria-label", "View product")}
                  data-testid="product-list-view-btn"
                  onClick={() => onViewProduct(product.id)}
                >
                  <VisibilityOutlinedIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default ProductListTable;
